import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { tradingServices, TradingPosition, TradingWallet } from '../../../services/trading-service'
import { isFeatureEnabled } from '../../../lib/utils/feature-flags'
import './public-portfolio.scss'

const usd = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/**
 * Public-market (Alpaca) portfolio. Fetches its own data and renders nothing
 * when the investor has no brokerage account or trading is unavailable.
 */
const PublicPortfolioSection = () => {
  const navigate = useNavigate()
  const [wallet, setWallet] = useState<TradingWallet | null>(null)
  const [positions, setPositions] = useState<TradingPosition[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const res = await tradingServices.getWallet()
      if (cancelled || res?.status !== 200) return
      setWallet(res.data)
      if (res.data.status === 'ACTIVE') {
        const pos = await tradingServices.getPositions()
        if (!cancelled && pos?.status === 200) setPositions(pos.data)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  if (!wallet) return null

  return (
    <section className='public-portfolio' data-testid='public-portfolio'>
      <div className='public-portfolio__head'>
        <h2>Public markets portfolio</h2>
        <div className='public-portfolio__actions'>
          {isFeatureEnabled('publicWallet') ? (
            <button type='button' onClick={() => navigate('/dashboard/wallet-public')}>
              Wallet
            </button>
          ) : null}
          {isFeatureEnabled('trading') ? (
            <>
              <button type='button' onClick={() => navigate('/dashboard/trading/portfolio')}>
                Portfolio
              </button>
              <button type='button' onClick={() => navigate('/dashboard/trading')}>
                Trade
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className='public-portfolio__stats'>
        <div>
          <span>Portfolio value</span>
          <strong>{usd(wallet.portfolioValue)}</strong>
        </div>
        <div>
          <span>Invested</span>
          <strong>{usd(wallet.investedValue)}</strong>
        </div>
        <div>
          <span>Cash</span>
          <strong>{usd(wallet.cash)}</strong>
        </div>
      </div>

      {positions.length === 0 ? (
        <p className='public-portfolio__empty'>No open positions.</p>
      ) : (
        <div className='public-portfolio__scroll'>
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Shares</th>
                <th>Avg cost</th>
                <th>Market value</th>
                <th>Unrealized P/L</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.symbol}>
                  <td>{p.symbol}</td>
                  <td>{p.qty}</td>
                  <td>{usd(p.avg_entry_price)}</td>
                  <td>{usd(p.market_value)}</td>
                  <td className={Number(p.unrealized_pl) < 0 ? 'is-loss' : 'is-gain'}>
                    {usd(p.unrealized_pl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export { PublicPortfolioSection }
